/* instant-nav.js — perceived-instant navigation.
 * Layer 2 (fallback to Speculation Rules in theme.liquid): prefetch a link's HTML
 * on user intent (hover / touchstart / mousedown) and for in-viewport nav links,
 * so the click resolves from cache. ~2KB, idle-scheduled, heavily guarded. */
(() => {
  'use strict';
  const supportsPrefetch = (() => {
    const l = document.createElement('link');
    return l.relList && l.relList.supports && l.relList.supports('prefetch');
  })();
  // If Speculation Rules prerendering is active, skip the JS fallback entirely.
  const hasSpecRules = HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');
  if (!supportsPrefetch || hasSpecRules) return;

  // --- guardrails: don't waste the user's data ---
  const conn = navigator.connection || {};
  if (conn.saveData) return;
  if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;

  const prefetched = new Set();
  const MAX = 50;                       // cap total prefetches per page
  const SAME = location.origin;
  let inflight = 0;
  const MAX_INFLIGHT = 3;

  const skip = (a) => {
    if (!a || !a.href || a.origin !== SAME) return true;
    if (prefetched.has(a.href) || prefetched.size >= MAX) return true;
    if (a.hasAttribute('data-no-prefetch')) return true;
    const p = a.pathname, q = a.search;
    if (/\/(cart|checkout|account|orders|admin)/.test(p)) return true;
    if (/(add|logout)/.test(p)) return true;
    if (q && /(sort_by|[?&]filter)/.test(q)) return true;     // skip faceted explosions
    if (a.getAttribute('rel') === 'nofollow') return true;
    if (a.target && a.target !== '_self') return true;
    return false;
  };

  const prefetch = (href) => {
    if (inflight >= MAX_INFLIGHT) return;
    prefetched.add(href);
    inflight++;
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    link.onload = link.onerror = () => { inflight--; };
    document.head.appendChild(link);
  };

  // --- intent: hover (desktop) + touchstart/mousedown (mobile / fast click) ---
  let hoverTimer;
  const onIntent = (e) => {
    const a = e.target.closest && e.target.closest('a');
    if (skip(a)) return;
    if (e.type === 'mouseenter') {
      hoverTimer = setTimeout(() => prefetch(a.href), 60); // ignore accidental passes
    } else {
      prefetch(a.href);                                    // touch/mousedown = high intent
    }
  };
  const cancel = () => clearTimeout(hoverTimer);
  document.addEventListener('mouseenter', onIntent, { capture: true, passive: true });
  document.addEventListener('mouseleave', cancel, { capture: true, passive: true });
  document.addEventListener('touchstart', onIntent, { capture: true, passive: true });
  document.addEventListener('mousedown', onIntent, { capture: true, passive: true });

  // --- viewport: prefetch above-the-fold nav links once idle ---
  const observeViewport = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (en.isIntersecting) {
          const a = en.target;
          if (!skip(a)) prefetch(a.href);
          io.unobserve(a);
        }
      }
    }, { rootMargin: '200px' });
    document.querySelectorAll('a[data-prefetch-viewport]').forEach((a) => io.observe(a));
  };
  (window.requestIdleCallback || ((f) => setTimeout(f, 200)))(observeViewport);
})();
